/**
 * useMenuPermissions Hook - Filter menu items based on user roles and permissions
 * Created: 1405/02/17
 */

import { useMemo } from 'react';
import { MenuItem, MergedMenuItem, MenuPermissions } from '../types';

interface User {
  id: string;
  role: string;
  permissions?: string[];
}

interface UseMenuPermissionsReturn {
  filteredMenu: MenuItem[];
  filteredTree: MergedMenuItem[];
  hasPermission: (permissions?: MenuPermissions) => boolean;
  hasMenuAccess: (menuId: string) => boolean;
}

export const useMenuPermissions = (
  menuItems: MenuItem[],
  menuTree: MergedMenuItem[],
  user: User | null
): UseMenuPermissionsReturn => {
  
  // Check if user has specific permissions
  const hasPermission = useMemo(() => {
    return (permissions?: MenuPermissions): boolean => {
      if (!user) return false;
      
      // If no permissions required, allow access
      if (!permissions) return true;
      
      // Check required roles
      if (permissions.requiredRoles && permissions.requiredRoles.length > 0) {
        const hasRequiredRole = permissions.requiredRoles.includes(user.role);
        if (!hasRequiredRole) return false;
      }
      
      // Check permission code
      if (permissions.view) {
        const hasViewPermission = user.permissions?.includes(permissions.view);
        if (!hasViewPermission) return false;
      }
      
      return true;
    };
  }, [user]);
  
  // Filter menu items based on permissions
  const filteredMenu = useMemo(() => {
    if (!user) return [];
    
    return menuItems.filter(item => {
      // Check visibility
      if (!item.isVisible || !item.isActive) return false;
      
      // Check permissions
      return hasPermission(item.permissions);
    });
  }, [menuItems, user, hasPermission]);
  
  // Filter tree recursively
  const filterTreeRecursive = (items: MergedMenuItem[]): MergedMenuItem[] => {
    return items
      .filter(item => {
        if (!item.isVisible || !item.isActive) return false;
        return hasPermission(item.permissions);
      })
      .map(item => ({
        ...item,
        children: item.children ? filterTreeRecursive(item.children) : []
      }));
  };
  
  const filteredTree = useMemo(() => {
    if (!user) return [];
    return filterTreeRecursive(menuTree);
  }, [menuTree, user, hasPermission]);
  
  // Check if user has access to specific menu
  const hasMenuAccess = useMemo(() => {
    return (menuId: string): boolean => {
      const item = menuItems.find(i => i.id === menuId);
      if (!item) return false;
      return hasPermission(item.permissions);
    };
  }, [menuItems, hasPermission]);
  
  return {
    filteredMenu,
    filteredTree,
    hasPermission,
    hasMenuAccess,
  };
};

export default useMenuPermissions;
